/**
 * Sentry initialisation with aggressive PII scrubbing.
 *
 * Privacy posture (must not change without legal sign-off):
 *   • Sentry DSN is read from VITE_SENTRY_DSN env var. If unset, Sentry is
 *     never loaded — the app runs without monitoring.
 *   • beforeSend strips every known PII field from the event before it
 *     leaves the browser:
 *       - All form values (we never send what the user typed)
 *       - All URL query strings + hash (may contain student IDs)
 *       - Free-text breadcrumbs from console.log
 *       - Anything that looks like an email or phone number
 *   • The user identifier sent is the Firebase UID only — never name, never
 *     email, never role.
 *   • Replays, performance traces, and session recording are DISABLED. We
 *     ship error events only.
 */

let _initialised = false;

export async function initSentry() {
  if (_initialised) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // Monitoring disabled - no DSN configured
  try {
    const Sentry = await import('@sentry/browser');
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_RELEASE || 'dev',
      // Errors only — no perf traces, no replay
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // PII scrubbing
      sendDefaultPii: false,
      beforeSend(event) {
        return scrubEvent(event);
      },
      beforeBreadcrumb(crumb) {
        // Drop console / fetch breadcrumbs that may contain free text
        if (crumb.category === 'console') return null;
        if (crumb.category === 'ui.input') return null;
        if (crumb.category === 'fetch' || crumb.category === 'xhr') {
          if (crumb.data?.url) crumb.data.url = redactUrl(crumb.data.url);
          delete crumb.data?.body;
          delete crumb.data?.response;
        }
        return crumb;
      },
    });
    _initialised = true;
    console.log('[sentry] initialised');
  } catch (err) {
    console.warn('[sentry] init failed', err);
  }
}

/** Redact a URL — strip query string and replace any UUID-like segments. */
function redactUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    u.search = '';
    u.hash = '';
    // Replace any path segment that looks like a Firestore document ID
    u.pathname = u.pathname.replace(/\/[A-Za-z0-9_-]{18,}/g, '/[id]');
    return u.toString();
  } catch { return '[url]'; }
}

/** Walk an event object and remove anything that smells like PII. */
function scrubEvent(event) {
  // Strip request body / form values
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    if (event.request.url) event.request.url = redactUrl(event.request.url);
    if (event.request.headers) {
      delete event.request.headers.cookie;
      delete event.request.headers.authorization;
    }
  }
  // User: only the Firebase UID, never email/name
  if (event.user) {
    event.user = { id: event.user.id || event.user.uid || 'anon' };
  }
  // Walk extra/contexts and redact email/phone patterns from any string value
  if (event.extra) event.extra = redactDeep(event.extra);
  if (event.contexts) event.contexts = redactDeep(event.contexts);
  if (event.tags) event.tags = redactDeep(event.tags);
  // Strip free-text from message
  if (event.message) event.message = redactString(event.message);
  // Strip URLs from breadcrumbs (already filtered above, but defence in depth)
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(b => {
      if (b.data?.url) b.data.url = redactUrl(b.data.url);
      if (b.message) b.message = redactString(b.message);
      return b;
    });
  }
  return event;
}

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const PHONE_RE = /\b(?:\+?61|0)[\s-]?\d(?:[\s-]?\d){7,9}\b/g;

function redactString(s) {
  if (typeof s !== 'string') return s;
  return s.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]');
}

function redactDeep(obj) {
  if (obj == null) return obj;
  if (typeof obj === 'string') return redactString(obj);
  if (Array.isArray(obj)) return obj.map(redactDeep);
  if (typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      // Drop fields that are obviously sensitive by name
      if (/(name|email|phone|address|dob|note|message|body|content|text|prompt|response)/i.test(k)) {
        out[k] = '[redacted]';
      } else {
        out[k] = redactDeep(v);
      }
    }
    return out;
  }
  return obj;
}

/** Set the Firebase UID on the active Sentry scope (no email, no name). */
export async function setSentryUser(uid) {
  if (!_initialised) return;
  try {
    const Sentry = await import('@sentry/browser');
    Sentry.setUser({ id: uid || 'anon' });
  } catch {}
}
